import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import {
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvButton,
  SalvModal,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxArchive, faDownload, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { ApiOrganisation } from '../api';
import { exportOrganisationConfig } from './organisationActions';
import './Organisations.scss';

const INCLUDED_AREAS = [
  'Foundation (organisation configuration, features, case statuses & rationales, risk ranges, alerting intervals, aggregate configurations, segments, dashboards, risk override reasons)',
  'Schemas',
  'Attribute mappings',
  'Custom lists & managed list links',
  'Query templates',
  'Risk rules, scenarios & alert decision rules',
  'Screening (clearance rules, screenable & context fields, segments)',
  'Alert handling (status options, note templates, alert filters, RFI screening/mapping/status config, bridge configuration)',
];

const COMING_SOON_AREAS = ['Integration endpoints (webhooks)'];

export interface ExportOrganisationModalProps {
  organisation: ApiOrganisation;
}

export const ExportOrganisationModal = ({ organisation }: ExportOrganisationModalProps) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isNameMatching = confirmName.trim() === organisation.name;
  const isDirtyAndInvalid = confirmName.length > 0 && !isNameMatching;

  const closeModal = () => {
    setShowModal(false);
    setConfirmName('');
  };

  const handleExport = async () => {
    if (!isNameMatching || organisation.id === undefined) {
      return;
    }
    setIsSubmitting(true);
    try {
      await dispatch(exportOrganisationConfig(organisation.id));
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <SalvButton
        aria-label={`Export ${organisation.name}`}
        icon={faDownload}
        label='Export'
        variant='muted'
        size={Size.EXTRA_SMALL}
        onClick={() => setShowModal(true)}
      />
      <SalvModal show={showModal} onHide={closeModal}>
        <div className='organisation-export-modal'>
          <SalvModal.Header>Export organisation configuration</SalvModal.Header>
          <SalvModal.Body>
            <Row>
              <Col md={12}>
                <p>
                  The configuration of <strong>{organisation.name}</strong> will be packaged as a JSON bundle.
                  Customers, alerts, transactions, users, and secrets (client secrets, private keys, webhook tokens) are
                  never included.
                </p>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <div className='bundle-scope-row'>
                  <div className='bundle-scope-icon'>
                    <FontAwesomeIcon icon={faBoxArchive} />
                  </div>
                  <div className='bundle-scope-body'>
                    <div className='bundle-scope-title'>Configuration bundle contents</div>
                    <div className='bundle-scope-group'>
                      <div className='bundle-scope-group-label'>Included in this export</div>
                      <div className='bundle-scope-subtitle'>{INCLUDED_AREAS.join(' · ')}</div>
                    </div>
                    <div className='bundle-scope-group'>
                      <div className='bundle-scope-group-label'>Not included yet</div>
                      <div className='bundle-scope-subtitle'>{COMING_SOON_AREAS.join(' · ')}</div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <SalvBanner
                  type={SalvBannerType.WARNING}
                  accent={SalvBannerAccent.LIGHT}
                  message='This bundle contains sensitive customer configuration. Store it on trusted systems only and share it through approved channels.'
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <SalvTextField
                  label={
                    <span>
                      Type <span className='fingerprint-chip'>{organisation.name}</span> to confirm
                    </span>
                  }
                  value={confirmName}
                  onChange={setConfirmName}
                  placeholder={organisation.name}
                  isInvalid={isDirtyAndInvalid}
                  size={Size.MEDIUM}
                  required={true}
                />
              </Col>
            </Row>
          </SalvModal.Body>
          <SalvModal.Footer>
            <SalvButton variant='secondary' label='Cancel' onClick={closeModal} disabled={isSubmitting} />
            <SalvButton
              variant='primary'
              label={isSubmitting ? 'Exporting…' : 'Export'}
              icon={isSubmitting ? faSpinner : faDownload}
              onClick={handleExport}
              disabled={!isNameMatching || isSubmitting}
            />
          </SalvModal.Footer>
        </div>
      </SalvModal>
    </React.Fragment>
  );
};

export default ExportOrganisationModal;
